/**
 * Dialogs: generic `modal` (macro in _components.html), the per-finding-row
 * action modal, the two run-confirmation modals, and the pre-expiry
 * session-timeout modal.
 *
 * Markup contract: finding rows carry data-signature and their dialogs'
 * titles are `finding-<kind>-<signature>-title`; confirm modals carry
 * a form[data-run-test-case-form] or form[data-run-form]; confirm buttons are
 * x-ref="confirmBtn". Run-now environment <option>s carry data-url,
 * data-persona-count, data-pages-estimate and data-busy-titles (JSON). The
 * session-timeout modal needs `#session-timeout-logout-form`.
 */

import { getCsrfToken } from "../csrf.js";
import { ensureFresh, refresh } from "../session-refresh.js";
import { focus_scope, read_json_attr } from "./helpers.js";

document.addEventListener("alpine:init", () => {
  Alpine.data("modal", () => ({
    ...focus_scope(),
    open: false,
    show() {
      this.capture();
      this.open = true;
      // The dialog is usually teleported to <body>, outside $root; the modal
      // macro marks it x-ref="dialog", which Alpine resolves across teleports.
      this.focusFirst(this.$refs.dialog);
    },
    hide() {
      this.open = false;
      this.restore();
    },
  }));

  // Per-finding-row modal: opens one of four dialogs (Create issue, Link
  // existing, Resolve, Create test) on the Health-tab open-findings list.
  // One scope per row so per-row modals can't interfere with each other
  // after an HTMX swap. Dialogs are mounted via `<template x-teleport="body">`
  // and listen for Escape; ``init()`` is preserved so server-rendered error
  // states (route returns 422 with the matching modal pre-opened) re-open
  // the right dialog on swap-in.
  Alpine.data("finding_action_modal", () => ({
    ...focus_scope(),
    // "create" | "link" | "resolve" | "create_test" | null. The dialogs'
    // title ids embed the kind with hyphens ("finding-create-test-<sig>-title").
    openKind: null,
    _signature: null,
    init() {
      // Cache the row's signature so show() can locate this row's teleported
      // dialog. This is the only place ``this.$el`` reliably points at the
      // x-data root row; inside a @click handler it is the clicked button.
      this._signature = this.$el.dataset.signature;
    },
    get openCreate() {
      return this.openKind === "create";
    },
    get openLink() {
      return this.openKind === "link";
    },
    get openResolve() {
      return this.openKind === "resolve";
    },
    get openCreateTest() {
      return this.openKind === "create_test";
    },
    show(kind) {
      this.capture();
      this.openKind = kind;
      if (!this._signature) return;
      const titleId = `finding-${kind.replace("_", "-")}-${this._signature}-title`;
      this.$nextTick(() => {
        const title = document.getElementById(titleId);
        const dialog = title?.closest('[role="dialog"]');
        if (dialog) this.focusFirst(dialog);
      });
    },
    hide() {
      this.openKind = null;
      this.restore();
    },
  }));

  Alpine.data("run_test_case_confirm", () => ({
    ...focus_scope(),
    open: false,
    envName: "",
    personaName: "",
    _form: null,
    init() {
      // Cache root data-* attrs and the form ref — `this.$el` is only the
      // scope root inside `init()`. See claude-design-system.md
      // "this.$el refers to the directive element" (confirm-modal teleport case).
      const rootEl = this.$el;
      this.envName = rootEl.dataset.envName || "";
      this.personaName = rootEl.dataset.personaName || "";
      this._form = rootEl.querySelector("form[data-run-test-case-form]");
    },
    show() {
      this.capture();
      this.open = true;
      this.focus(this.$refs.confirmBtn);
    },
    hide() {
      this.open = false;
      this.restore();
    },
    confirm() {
      if (this._form) this._form.requestSubmit();
    },
  }));

  Alpine.data("run_now_confirm", () => ({
    ...focus_scope(),
    open: false,
    envName: "",
    envUrl: "",
    runModeLabel: "",
    runModeValue: "",
    personaCount: 1,
    estimatedPages: null,
    // Selected env's in-flight launches by run mode → disabled-button title.
    _busyTitles: {},
    _form: null,
    init() {
      // Cache root data-* attrs and the form ref — `this.$el` is only the
      // scope root inside `init()`. See claude-design-system.md
      // "this.$el refers to the directive element" (confirm-modal teleport case).
      const rootEl = this.$el;
      this._form = rootEl.querySelector("form[data-run-form]");

      const envSelect = rootEl.querySelector('select[name="environment_name"]');
      const modeSelect = rootEl.querySelector('select[name="run_mode"]');
      if (envSelect) {
        this._syncEnv(envSelect);
        envSelect.addEventListener("change", () => {
          this._syncEnv(envSelect);
        });
      }
      if (modeSelect) {
        this._syncMode(modeSelect);
        modeSelect.addEventListener("change", () => {
          this._syncMode(modeSelect);
        });
      }
    },
    _syncEnv(sel) {
      this.envName = sel.value;
      const opt = sel.options[sel.selectedIndex];
      if (opt) {
        this.envUrl = opt.dataset.url || "";
        // Trust the server-rendered count: 0 means "no active personas" and
        // must surface as such (Start run is disabled). Coercing 0 → 1 hides
        // the no-personas state behind a misleading "1 persona" readback.
        const count = parseInt(opt.dataset.personaCount, 10);
        this.personaCount = Number.isNaN(count) || count < 0 ? 0 : count;
        const pagesRaw = opt.dataset.pagesEstimate;
        if (pagesRaw === undefined || pagesRaw === "") {
          this.estimatedPages = null;
        } else {
          const pages = parseInt(pagesRaw, 10);
          this.estimatedPages = Number.isNaN(pages) ? null : pages;
        }
        this._busyTitles = read_json_attr(opt, "busyTitles", {});
      } else {
        this.envUrl = "";
        this.personaCount = 0;
        this.estimatedPages = null;
        this._busyTitles = {};
      }
    },
    _syncMode(sel) {
      const opt = sel.options[sel.selectedIndex];
      this.runModeLabel = opt ? (opt.textContent || "").trim() : sel.value;
      this.runModeValue = sel.value;
    },
    get isExploreMode() {
      return this.runModeValue === "exploration";
    },
    get modeArticle() {
      // Pick "a"/"an" based on the run-mode label's first letter so the modal
      // body reads naturally for any mode label ("an Explore", "a Test only").
      return /^[aeiou]/i.test(this.runModeLabel) ? "an" : "a";
    },
    get personaLabel() {
      return this.personaCount === 1 ? "persona" : "personas";
    },
    get hasPersonas() {
      return this.personaCount > 0;
    },
    get showPagesClause() {
      return this.isExploreMode && this.estimatedPages !== null && this.hasPersonas;
    },
    // Run now locks only against a duplicate launch: one already in flight in
    // the selected environment and mode (the server excludes PR verifications).
    get runNowBlockedTitle() {
      return this._busyTitles[this.runModeValue] ?? null;
    },
    get runNowBlocked() {
      return this.runNowBlockedTitle !== null;
    },
    show() {
      this.capture();
      this.open = true;
      this.focus(this.$refs.confirmBtn);
    },
    hide() {
      this.open = false;
      this.restore();
    },
    confirm() {
      // Guard against empty-environment submissions: if the selected env has
      // no active personas, the Start run button is x-bind:disabled and the
      // modal shows a no-personas message — but defend in depth here too.
      if (!this.hasPersonas) return;
      if (this._form) this._form.requestSubmit();
    },
  }));

  // Pre-expiry session-timeout modal.
  // Polls /auth/session-status every 60s; fires the modal when the refresh
  // family's absolute cap is within warn_below_seconds of expiry. The modal
  // exposes "Stay signed in" (force a /auth/refresh POST) and "Sign out".
  Alpine.data("session_timeout_modal", () => ({
    open: false,
    timeRemaining: 0,
    _interval: null,
    _countdown: null,
    init() {
      this._poll();
      this._interval = setInterval(() => this._poll(), 60 * 1000);
    },
    async _poll() {
      try {
        const resp = await fetch("/auth/session-status", {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        if (resp.status !== 200) return;
        const data = await resp.json();
        // Proactive access-token refresh (#3424): rotate the session silently
        // *before* the short-lived access JWT lapses, so the next background
        // poll (e.g. the 45s topbar bell) never sees an expired token. This is
        // orthogonal to the family-cap warning below — no modal, no countdown.
        this._maybeProactiveRefresh(data);
        if (data.seconds_remaining <= data.warn_below_seconds && data.seconds_remaining > 0) {
          this.timeRemaining = data.seconds_remaining;
          this._show();
        } else if (this.open && data.seconds_remaining > data.warn_below_seconds) {
          this._hide();
        }
      } catch (_e) {
        /* swallow — pollers should never crash the page */
      }
    },
    _maybeProactiveRefresh(data) {
      const remaining = data.access_seconds_remaining;
      const threshold = data.access_refresh_below_seconds;
      // No decodable access cookie (null) or no server-owned threshold → skip.
      if (typeof remaining !== "number" || typeof threshold !== "number") return;
      // Already lapsed (0) is left to the reactive silent-refresh path; only
      // pre-empt while the token is still valid but inside the horizon.
      if (remaining <= 0 || remaining > threshold) return;
      // Only from a foregrounded tab: a hidden tab has no in-flight poll to
      // protect, and refreshing from every background tab at once is the very
      // multi-tab reuse storm the cross-tab single-flight (session-refresh.js)
      // exists to prevent.
      if (document.hidden) return;
      // Fire-and-forget the deduped POST /auth/refresh. A decline is handled
      // by the passive htmx:responseError path on the next real request, so
      // there is nothing to do with the result here.
      refresh().catch(() => {});
    },
    _show() {
      if (this.open) return;
      this.open = true;
      this._countdown = setInterval(() => {
        this.timeRemaining = Math.max(0, this.timeRemaining - 1);
        if (this.timeRemaining === 0) {
          this._hide();
          window.location.assign("/auth/login?reason=session_expired");
        }
      }, 1000);
    },
    _hide() {
      this.open = false;
      if (this._countdown) {
        clearInterval(this._countdown);
        this._countdown = null;
      }
    },
    stayActive() {
      ensureFresh(() => {
        this._hide();
        this._poll();
      }, "/auth/login");
    },
    signOut() {
      const form = document.getElementById("session-timeout-logout-form");
      if (!form) return;
      // The CSRF cookie has a 1h Max-Age, but the user may have left the
      // tab idle past that. Re-read the cookie now so a stale page-load
      // token doesn't bounce the logout with a CSRF error.
      const csrfInput = form.querySelector('input[name="fastapi-csrf-token"]');
      const fresh = getCsrfToken() || "";
      if (csrfInput) csrfInput.value = fresh;
      form.requestSubmit();
    },
    destroy() {
      if (this._interval) clearInterval(this._interval);
      this._interval = null;
      this._hide();
    },
  }));
});
